import React, { useEffect, useRef } from 'react';

import type { CustomizeSidebarMenuItem } from '../../types';

type StandaloneButtonMenuItemParams = {
	item: CustomizeSidebarMenuItem;
	otherProps?: Record<string, any>;
};

/**
 * If the More menu trigger button or Customize Sidebar button is a ButtonMenuItem or similar (which we expect them to be),
 * they come wrapped in a <div role="listitem"> by default.
 * Sometimes we don't want them to be wrapped in a listitem because that means we need another level of List wrapper
 * which is otherwise be superfluous and can lead to confusing screen reader announcements.
 *
 * By controlling this centrally where they are used, we avoid complicating integrations with extra requirements
 * for the component they pass in, thus reducing the risk of them inadvertently introducing invalid HTML/a11y violations.
 * The one exception to this is when the component is not directly a ButtonMenuItem or similar,
 * such as when it is an entrypoint ModalTrigger with a ButtonMenuItem inside it.
 * In that case, they just need to apply the listItemRef we provide to the ButtonMenuItem.
 * For examples of how to do this:
 * @see ./platform/packages/navigation/global-more-menu/test-helpers/index.tsx
 * @see ./platform/packages/navigation/global-more-menu/stories/global-more-menu.stories.tsx
 * @see ./platform/packages/navigation/global-more-menu/examples/04-return-focus-demo.tsx
 * @see ./platform/packages/navigation/global-more-menu/examples/05-return-focus-demo-more-menu.tsx
 *
 * @param item - The menu item ReactNode to render, which may be wrapped in a div with role="listitem" by default.
 * @param otherProps - Other props such as returnFocusRef to pass down.
 */
export function useStandaloneButtonMenuItem({
	item,
	otherProps = {},
}: StandaloneButtonMenuItemParams): React.ReactNode {
	const listItemRef = useRef<HTMLDivElement>(null);

	const itemWithRef = React.isValidElement(item)
		? React.cloneElement(item, {
				...otherProps,
				listItemRef,
			})
		: item;

	useEffect(() => {
		listItemRef?.current?.removeAttribute('role');
	}, []);

	return itemWithRef;
}
